import { Platform } from '../platform';

/**
 * Tracks whether focus is currently arriving via Tab — the only keyboard path
 * into a text field. Drives the keyboard-only focus ring on Input / Textarea.
 *
 * `:focus-visible` can't answer this: per the CSS spec's UA heuristics a text
 * field matches it on EVERY focus (it always takes keyboard input), so click
 * and programmatic `.focus()` would ring too.
 *
 * "Not pointer-initiated" can't answer it either — that's true of programmatic
 * `.focus()` as well, which is how a dialog autofocuses its search field
 * (global search, ⌘K). Only "a Tab keydown is in flight" separates the three.
 *
 * Capture phase, at module scope: the Tab keydown must be observed before the
 * browser moves focus as the keydown's default action, and before any handler
 * can stop it propagating. On native the listeners never register, so
 * `isTabInitiatedFocus()` is permanently false — touch is the only focus path
 * there and it never rings.
 */
let tabInitiated = false;

if (Platform.OS === 'web' && typeof window !== 'undefined') {
  window.addEventListener(
    'keydown',
    (event) => {
      tabInitiated = event.key === 'Tab';
    },
    true,
  );
  // A pointer press supersedes any earlier Tab, so click-to-focus stays ringless.
  window.addEventListener(
    'pointerdown',
    () => {
      tabInitiated = false;
    },
    true,
  );
}

export const isTabInitiatedFocus = () => tabInitiated;
